import React, { useState } from 'react';
import { updateUserProfile } from '../services/api';

const INTENDED_TIER_KEY = 'sigmacore_intended_tier';

/**
 * Profile-completion screen (F1).
 *
 * Clerk owns authentication, but `company_name` is a required Eliza
 * profile field that the Clerk JWT doesn't carry. On first login the
 * backend creates the user row with company_name='' and flags the
 * response with X-Profile-Incomplete; App.js routes here until the
 * profile is filled in.
 *
 * The intended tier (?plan=<tier> stashed by AuthPage) is read back here
 * purely for display continuity with the marketing handoff — Stripe
 * checkout (F6) consumes it separately.
 */
export default function SignupComplete({ onComplete }) {
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const intendedTier = (() => {
    try {
      return localStorage.getItem(INTENDED_TIER_KEY);
    } catch {
      return null;
    }
  })();

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!companyName.trim()) {
      setError('Company name is required.');
      return;
    }
    setBusy(true);
    try {
      await updateUserProfile({
        company_name: companyName.trim(),
        phone: phone.trim() || null,
      });
      // Tier handoff has served its purpose; clear it so a later
      // sign-in doesn't resurrect a stale plan choice.
      try {
        localStorage.removeItem(INTENDED_TIER_KEY);
      } catch { /* ignore */ }
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>SigmaCore.ai</h1>
        <h2>Complete your profile</h2>
        <p className="auth-subtitle">
          Just one more step before we start building your plan.
        </p>

        {intendedTier && (
          <div className="auth-info" role="status">
            You selected the <strong>{intendedTier}</strong> plan.
          </div>
        )}

        <form onSubmit={submit} className="auth-form">
          <label>
            Company name
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              autoComplete="organization"
              required
            />
          </label>
          <label>
            Phone <span className="optional">(optional)</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
            />
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
